import { Injectable } from '@angular/core';

export interface PricingFeature {
  label: string;
  isCheckbox?: boolean;
  checkboxLabel?: string;
  /**
   * When true, this feature is tucked behind the card's
   * "Show all features" toggle instead of shown by default.
   * Keeps each card scannable at a glance; nothing is lost.
   */
  hidden?: boolean;
}

export interface PricingPlan {
  id: string;
  name: string;
  tagline: string;
  icon: string;
  monthlyPrice: number | null;
  yearlyPrice: number | null;
  isFeatured: boolean;
  ctaLabel: string;
  ctaStyle: 'primary' | 'outline' | 'ghost' | 'contact';
  ctaLink: string;
  badge?: string;
  creditsLabel: string;
  creditsSubtext: string;
  creditsPerMonth: number;
  features: PricingFeature[];
  razorpayMonthlyPlanId?: string;
  razorpayYearlyPlanId?: string;
}

export interface EnterprisePlan {
  features: PricingFeature[];
}

@Injectable({ providedIn: 'root' })
export class PlansService {
  // ─────────────────────────────────────────────────────────────────
  // SINGLE SOURCE OF TRUTH
  //
  // WHAT THE PLATFORM ACTUALLY DOES (from backend docs):
  //   5 AI agents run sequentially per prompt:
  //     1. Analysis Agent: turns prompt into a JSON spec (pages, models, nav, colors)
  //     2. Design Agent: generates design system (Tailwind tokens, fonts, style)
  //     3. Image Agent: Vertex AI Imagen or professional placeholders
  //     4. Generation Agent: writes full Angular source code
  //     5. Vercel Deploy: pushes to GitHub, triggers Vercel, returns live URL
  //   If Vercel build fails, Repair Agent reads logs, applies surgical fixes, redeploys.
  //   Jobs stream real-time logs via Firestore subcollections.
  //   Every project gets a GitHub repo and a Vercel deployment.
  //   Iterative updates: same project_id + session_id gives the AI full context.
  //   Instant rollback: alias swap to any previous Vercel deployment, no rebuild.
  //   File and screenshot attachments via Firebase Storage.
  //   Download project as ZIP anytime.
  //
  // CREDIT ECONOMICS (GCP Gemini data, Dec 2025 to Mar 2026):
  //   API cost: ₹2.00 / credit | Avg prompt: ~47 credits | Max first-build: 124 credits
  //   Free:    125 lifetime   → ₹250 one-time CAC
  //   Starter: ₹399/mo        → 125 cr/mo → margin ₹149/mo (37%)
  //   Builder: ₹1,299/mo      → 600 cr/mo → margin ₹99/mo (8%)
  //   Pro:     ₹3,499/mo      → 1,600 cr/mo → margin ₹299/mo (9%)
  //
  // FEATURE LISTS: each tier's `features` array is fully self-contained.
  // No "Everything in X, plus" shorthand: every card explicitly lists its
  // complete feature set, even where an item repeats verbatim from the
  // tier below. Comparing columns left to right should never require
  // remembering what an earlier card said.
  // ─────────────────────────────────────────────────────────────────

  readonly individualPlans: PricingPlan[] = [
    {
      id: 'free',
      name: 'Free',
      tagline: 'See the full pipeline in action. No card needed.',
      icon: '⚡',
      monthlyPrice: 0,
      yearlyPrice: 0,
      isFeatured: false,
      ctaLabel: 'Start for Free',
      ctaStyle: 'ghost',
      ctaLink: '/signup',
      creditsLabel: '125 lifetime credits',
      creditsSubtext: 'Enough to ship 1 to 2 complete Angular apps',
      creditsPerMonth: 125,
      razorpayMonthlyPlanId: undefined,
      razorpayYearlyPlanId: undefined,
      features: [
        { label: '5-agent pipeline builds and deploys your app' },
        { label: 'Auto-repair fixes failed builds automatically' },
        { label: 'Live URL on every generation' },
        { label: 'AI remembers your full project context' },
        { label: 'Real-time streaming build logs', hidden: true },
        { label: 'Attach files and screenshots to any prompt', hidden: true },
      ],
    },
    {
      id: 'starter',
      name: 'Starter',
      tagline: 'Keep iterating every month. Product Builder included.',
      icon: '🔷',
      monthlyPrice: 399,
      yearlyPrice: 339,
      isFeatured: false,
      ctaLabel: 'Get Started',
      ctaStyle: 'outline',
      ctaLink: '/checkout?plan=starter',
      creditsLabel: '125 credits / month',
      creditsSubtext: '~2 to 3 full Angular app generations per month',
      creditsPerMonth: 125,
      razorpayMonthlyPlanId: 'plan_SPeulQduwkuKMD',
      razorpayYearlyPlanId: 'plan_SPevoTZ4asaXac',
      features: [
        { label: '125 fresh credits every month' },
        { label: 'Team chat & shared board with your Product Builder' },
        { label: 'Public project hosting' },
        { label: 'Cancel any running generation instantly' },
        { label: '5-agent pipeline builds and deploys your app', hidden: true },
        { label: 'Auto-repair fixes failed builds automatically', hidden: true },
        { label: 'Live URL on every generation', hidden: true },
        { label: 'AI remembers your full project context', hidden: true },
        { label: 'Real-time streaming build logs', hidden: true },
        { label: 'Attach files and screenshots to any prompt', hidden: true },
        { label: 'Session continuity across every project', hidden: true },
        { label: 'Top up credits anytime', hidden: true },
        { label: 'Download project as ZIP anytime', hidden: true },
        { label: 'Priority email support', hidden: true },
        {
          label: 'devservices',
          isCheckbox: true,
          checkboxLabel: 'Product Builder on standby, raise requests anytime',
        },
      ],
    },
    {
      id: 'builder',
      name: 'Builder',
      tagline: 'Ship to your domain. Roll back in one click. Product Builder included.',
      icon: '🔺',
      monthlyPrice: 1299,
      yearlyPrice: 1199,
      isFeatured: true,
      ctaLabel: 'Start Building',
      ctaStyle: 'primary',
      ctaLink: '/checkout?plan=builder',
      badge: 'Most Popular',
      creditsLabel: '600 credits / month',
      creditsSubtext: '~12 to 13 full Angular app generations per month',
      creditsPerMonth: 600,
      razorpayMonthlyPlanId: 'plan_SPfkmdjNNvk8Sb',
      razorpayYearlyPlanId: 'plan_SPflXm8t86LhhP',
      features: [
        { label: '600 credits / month, 5x Starter' },
        { label: 'Team chat & shared board with your Product Builder' },
        { label: 'Deploy to your own custom domain' },
        { label: 'Instant rollback to any previous deployment' },
        { label: '5-agent pipeline builds and deploys your app', hidden: true },
        { label: 'Auto-repair fixes failed builds automatically', hidden: true },
        { label: 'Live URL on every generation', hidden: true },
        { label: 'AI remembers your full project context', hidden: true },
        { label: 'Real-time streaming build logs', hidden: true },
        { label: 'Attach files and screenshots to any prompt', hidden: true },
        { label: 'Public project hosting', hidden: true },
        { label: 'Cancel any running generation instantly', hidden: true },
        { label: 'Session continuity across every project', hidden: true },
        { label: 'Top up credits anytime', hidden: true },
        { label: 'Download project as ZIP anytime', hidden: true },
        { label: 'Priority email support', hidden: true },
        { label: 'Private GitHub repo per project', hidden: true },
        { label: '1-click redeploy from any commit', hidden: true },
        { label: 'Private hosting with SSL', hidden: true },
        {
          label: 'devservices',
          isCheckbox: true,
          checkboxLabel: 'Product Builder on standby, raise requests anytime',
        },
      ],
    },
    {
      id: 'pro',
      name: 'Pro',
      tagline: 'Maximum throughput. White-label. Full history. Product team included.',
      icon: '◆',
      monthlyPrice: 3499,
      yearlyPrice: 3199,
      isFeatured: false,
      ctaLabel: 'Go Pro',
      ctaStyle: 'outline',
      ctaLink: '/checkout?plan=pro',
      creditsLabel: '1,600 credits / month',
      creditsSubtext: '~33 to 34 full Angular app generations per month',
      creditsPerMonth: 1600,
      razorpayMonthlyPlanId: 'plan_SPfm19fBRi5rsL',
      razorpayYearlyPlanId: 'plan_SPfmc6IyNRwbGD',
      features: [
        { label: '1,600 credits / month, 13x Starter' },
        { label: 'Team chat & shared board with your Product Builder' },
        { label: 'White-label hosting on your own domain' },
        { label: 'Advanced Angular: SSR and hydration' },
        { label: '5-agent pipeline builds and deploys your app', hidden: true },
        { label: 'Auto-repair fixes failed builds automatically', hidden: true },
        { label: 'Live URL on every generation', hidden: true },
        { label: 'AI remembers your full project context', hidden: true },
        { label: 'Real-time streaming build logs', hidden: true },
        { label: 'Attach files and screenshots to any prompt', hidden: true },
        { label: 'Public project hosting', hidden: true },
        { label: 'Cancel any running generation instantly', hidden: true },
        { label: 'Session continuity across every project', hidden: true },
        { label: 'Top up credits anytime', hidden: true },
        { label: 'Download project as ZIP anytime', hidden: true },
        { label: 'Priority email support', hidden: true },
        { label: 'Deploy to your own custom domain', hidden: true },
        { label: 'Instant rollback to any previous deployment', hidden: true },
        { label: 'Private GitHub repo per project', hidden: true },
        { label: '1-click redeploy from any commit', hidden: true },
        { label: 'Private hosting with SSL', hidden: true },
        { label: 'Full deployment history with rollback', hidden: true },
        { label: 'Multi-module architecture patterns', hidden: true },
        { label: 'Dedicated Slack channel for your team', hidden: true },
        { label: 'Early access to new features', hidden: true },
        {
          label: 'devservices',
          isCheckbox: true,
          checkboxLabel: 'Product Builder on standby, raise requests anytime',
        },
      ],
    },
  ];

  readonly enterprisePlan: EnterprisePlan = {
    features: [
      { label: 'Custom monthly credit pool' },
      { label: 'Dedicated product squad, not just one Product Builder' },
      { label: 'Single sign-on and role-based access' },
      { label: 'Team chat & shared board with your Product Builder' },
      { label: '1,600+ credits / month, scaled to your org', hidden: true },
      { label: 'White-label hosting on your own domain', hidden: true },
      { label: 'Advanced Angular: SSR and hydration', hidden: true },
      { label: 'Full deployment history with rollback', hidden: true },
      { label: 'Multi-module architecture patterns', hidden: true },
      { label: 'Dedicated Slack channel for your team', hidden: true },
      { label: 'Early access to new features', hidden: true },
      { label: 'On-premise or private cloud deployment option', hidden: true },
      { label: 'Custom SLA and uptime guarantees', hidden: true },
      { label: 'Quarterly architecture review and code audit', hidden: true },
      {
        label: 'devservices',
        isCheckbox: true,
        checkboxLabel: 'Product Builder on standby, raise requests anytime',
      },
    ],
  };

  get paidPlans(): PricingPlan[] {
    return this.individualPlans.filter(
      (p) => p.monthlyPrice !== null && p.monthlyPrice > 0,
    );
  }

  getPlanById(id: string): PricingPlan | undefined {
    return this.individualPlans.find((p) => p.id === id);
  }

  getDisplayPrice(plan: PricingPlan, yearly: boolean): string {
    if (plan.monthlyPrice === null) return 'Custom';
    if (plan.monthlyPrice === 0) return '₹0';
    const price = yearly ? plan.yearlyPrice! : plan.monthlyPrice;
    return `₹${price.toLocaleString('en-IN')}`;
  }

  getAnnualSavings(plan: PricingPlan): number | null {
    if (!plan.monthlyPrice || plan.monthlyPrice === 0) return null;
    return (plan.monthlyPrice - plan.yearlyPrice!) * 12;
  }

  getRazorpayPlanId(plan: PricingPlan, yearly: boolean): string | undefined {
    return yearly ? plan.razorpayYearlyPlanId : plan.razorpayMonthlyPlanId;
  }
}